import React, { useEffect, useState } from 'react';
import {
  ArrowDown, ArrowUp, Edit3, Eye, EyeOff, Image as ImgIcon, Layers, LayoutGrid,
  Loader2, Monitor, Plus, Smartphone, Trash2, Upload, X,
} from 'lucide-react';
import client, { resolveApiAssetUrl } from '../lib/api';
import { UPLOAD_MAX_LABEL, uploadTooLarge } from '../lib/mediaAsset';
import { useToast } from '../hooks/use-toast';
import { cardMatchesDevice } from '../lib/homeCategories';

const emptyCard = {
  card_key: '',
  title: '',
  subtitle: '',
  cta_text: 'Hemen Oyna',
  href: '',
  image: '',
  game_category: '',
  device: 'all',
  order: 0,
  is_active: true,
};

// Kart hangi oyun kategorisine bağlanırsa, o kategori panelden kapatıldığında
// (ya da kataloğu boşaldığında) kart da ana sayfada gösterilmez.
const gameCategoryOptions = [
  ['', 'Bağımsız — her zaman görünür'],
  ['slot', 'Slot oyunları'],
  ['live-casino', 'Canlı casino'],
  ['crash', 'Crash / hızlı oyunlar'],
  ['sports', 'Spor bahisleri'],
];

// Klasik temada kartın ikonu bu anahtardan seçilir; Merit temasında ikon yoktur.
const cardKeyOptions = [
  ['', 'İkonsuz'],
  ['slot', 'Slot (zar)'],
  ['canli', 'Canlı casino (kamera)'],
  ['spor', 'Spor (kupa)'],
  ['crash', 'Crash (roket)'],
  ['bonus', 'Bonus (hediye)'],
];

// Aynı alanda masaüstünde bir set, telefonda başka bir set gösterilebilir
// (Betrun: masaüstünde 5 dikey kart, mobilde 8 ikon kutusu). Sınır
// lib/homeCategories.js MOBILE_QUERY'dedir (1024 px ve altı mobil).
const deviceOptions = [
  ['all', 'Her cihaz'],
  ['desktop', 'Yalnızca masaüstü'],
  ['mobile', 'Yalnızca mobil'],
];

// Liste süzgeci "bu cihazda ne görünür" sorusunu yanıtlar: masaüstü seçiliyken
// "Her cihaz" kartları da listelenir, çünkü sitede o sette onlar da durur.
// Kural sitenin kendi süzgecidir (cardMatchesDevice); ayrışırsa panel bir set,
// site başka set gösterirdi. Tercih tarayıcıda durur.
const DEVICE_FILTERS = [
  ['', 'Tümü', Layers],
  ['desktop', 'Masaüstü', Monitor],
  ['mobile', 'Mobil', Smartphone],
];
const DEVICE_FILTER_KEY = 'admin:home-categories-device';

const readDeviceFilter = () => {
  try {
    const value = window.localStorage.getItem(DEVICE_FILTER_KEY) || '';
    return DEVICE_FILTERS.some(([id]) => id === value) ? value : '';
  } catch {
    return '';
  }
};

const writeDeviceFilter = (value) => {
  try {
    window.localStorage.setItem(DEVICE_FILTER_KEY, value);
  } catch {
    /* gizli sekme: tercih yalnızca bu oturumda kalır */
  }
};

const deviceLabel = (value) => (
  deviceOptions.find(([id]) => id === value)?.[1] || 'Her cihaz'
);

const gameCategoryLabel = (value) => (
  gameCategoryOptions.find(([id]) => id === value)?.[1] || 'Bağımsız'
);

const HomeCategoryCards = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyCard);
  const [busy, setBusy] = useState(false);
  const [orderBusy, setOrderBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deviceFilter, setDeviceFilter] = useState(readDeviceFilter);
  const { toast } = useToast();

  const visibleItems = deviceFilter
    ? items.filter((card) => cardMatchesDevice(card, deviceFilter))
    : items;
  const countFor = (device) => (
    device ? items.filter((card) => cardMatchesDevice(card, device)).length : items.length
  );
  const chooseDeviceFilter = (value) => {
    setDeviceFilter(value);
    writeDeviceFilter(value);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get('/admin/home-categories');
      setItems(data.items || []);
    } catch {
      toast({ title: 'Kartlar yüklenemedi' });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => {
    setForm({ ...emptyCard, order: items.length + 1 });
    setModal({ mode: 'new' });
  };
  const openEdit = (card) => {
    setForm({ ...emptyCard, ...card });
    setModal({ mode: 'edit', id: card.id });
  };

  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (uploadTooLarge(file)) {
      toast({ title: 'Görsel yüklenemedi', description: `Dosya en fazla ${UPLOAD_MAX_LABEL} olabilir.` });
      return;
    }
    setUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const { data } = await client.post('/admin/uploads/images', body);
      setForm((current) => ({ ...current, image: data.url }));
      toast({ title: 'Kart görseli yüklendi' });
    } catch (error) {
      toast({
        title: 'Görsel yüklenemedi',
        description: error.response?.data?.detail || 'Dosya yükleme başarısız.',
      });
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast({ title: 'Başlık zorunlu' });
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, href: form.href || '/' };
      if (modal.mode === 'new') await client.post('/admin/home-categories', payload);
      else await client.patch(`/admin/home-categories/${modal.id}`, payload);
      toast({ title: 'Kaydedildi' });
      setModal(null);
      load();
    } catch (error) {
      toast({
        title: 'Hata',
        description: error.response?.data?.detail || 'Başarısız',
      });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (card) => {
    if (!window.confirm(`"${card.title}" kartı silinsin mi?`)) return;
    try {
      await client.delete(`/admin/home-categories/${card.id}`);
      toast({ title: 'Silindi' });
      load();
    } catch (error) {
      toast({ title: 'Hata', description: error.response?.data?.detail || 'Silinemedi' });
    }
  };

  const toggle = async (card) => {
    try {
      await client.patch(`/admin/home-categories/${card.id}`, { is_active: !card.is_active });
      load();
    } catch {
      toast({ title: 'Durum değiştirilemedi' });
    }
  };

  // Sıra tek istekte yazılır; başarısız olursa listeyi sunucudan tazeleriz,
  // yoksa ekranda gerçekte kaydedilmemiş bir sıra kalırdı. Süzgeç açıkken kart
  // görünen listedeki komşusuyla yer değiştirir: aradaki gizli kartlar (diğer
  // cihazın seti) yerinde kalır, yani o setin sırası bozulmaz.
  const move = async (card, direction) => {
    const position = visibleItems.findIndex((item) => item.id === card.id);
    const neighbour = visibleItems[position + direction];
    if (orderBusy || position < 0 || !neighbour) return;
    const index = items.findIndex((item) => item.id === card.id);
    const target = items.findIndex((item) => item.id === neighbour.id);
    const reordered = [...items];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setItems(reordered.map((card, position) => ({ ...card, order: position + 1 })));
    setOrderBusy(true);
    try {
      await client.patch('/admin/home-categories/reorder', {
        ordered_ids: reordered.map((card) => card.id),
      });
    } catch (error) {
      toast({
        title: 'Sıra kaydedilemedi',
        description: error.response?.data?.detail || 'Önceki sıra geri yüklendi',
      });
      load();
    } finally {
      setOrderBusy(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div>
          <h2 className="font-bold">Ana Sayfa Kategori Kartları</h2>
          <p className="text-xs text-slate-400">
            Ana sayfadaki SLOT / CANLI CASINO / SPOR kartlarının görseli, yazısı,
            bağlantısı ve sırası. Her iki tema da bu listeyi kullanır.
          </p>
        </div>
        <button onClick={openNew} className="admin-btn-primary ml-auto flex items-center gap-2">
          <Plus size={14} /> Yeni Kart
        </button>
      </div>

      <div className="admin-filter-row mb-2">
        <div className="admin-filter-scroll" role="group" aria-label="Cihaz süzgeci">
          {DEVICE_FILTERS.map(([id, label, Icon]) => (
            <button
              key={id || 'all'}
              type="button"
              onClick={() => chooseDeviceFilter(id)}
              aria-pressed={deviceFilter === id}
              className={`admin-tab ${deviceFilter === id ? 'active' : ''}`}
            >
              <Icon size={13} /> {label} ({countFor(id)})
            </button>
          ))}
        </div>
      </div>
      <p className="mb-4 text-[11px] text-slate-500">
        {deviceFilter
          ? `${deviceFilter === 'desktop' ? 'Masaüstünde' : 'Mobilde'} görünen set listeleniyor. “Her cihaz” kartları iki sette de yer alır; oklar kartı yalnızca bu setin içinde taşır.`
          : 'Masaüstü ve mobil setleri ayrı kontrol etmek için yukarıdan cihaz seçin.'}
      </p>

      {loading ? (
        <div className="admin-card p-10 text-center"><Loader2 className="inline animate-spin text-cyan-400" /></div>
      ) : items.length === 0 ? (
        <div className="admin-card p-10 text-center text-slate-500">
          Kart yok — ana sayfada kategori bölümü gösterilmiyor.
        </div>
      ) : visibleItems.length === 0 ? (
        <div className="admin-card p-10 text-center text-slate-500">
          {deviceFilter === 'desktop' ? 'Masaüstünde' : 'Mobilde'} görünen kart yok.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleItems.map((card, index) => (
            <div key={card.id} className="admin-card overflow-hidden">
              {/* Önizleme sitedeki kart oranıyla aynıdır; panelde düzgün duran
                  bir görselin sitede kırpılması böylece şaşırtmaz. */}
              <div className="relative aspect-[16/7] bg-slate-800">
                {card.image ? (
                  <img src={resolveApiAssetUrl(card.image)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <ImgIcon size={36} className="absolute inset-0 m-auto text-slate-600" />
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/40 to-transparent" />
                <div className="absolute bottom-3 left-3">
                  <div className="text-2xl font-black text-white leading-none">{card.title}</div>
                  <div className="text-xs font-bold tracking-[2px] text-slate-300 mt-1">{card.subtitle}</div>
                </div>
                <div className="absolute top-3 right-3 flex gap-1">
                  <span className={`badge-pill ${card.is_active ? 'badge-green' : 'badge-gray'}`}>
                    {card.is_active ? 'Aktif' : 'Pasif'}
                  </span>
                  <span className="badge-pill badge-gray">{deviceLabel(card.device)}</span>
                  <span className="badge-pill badge-gray">#{items.indexOf(card) + 1}</span>
                </div>
              </div>
              <div className="p-4 space-y-2">
                <div className="text-xs text-slate-400">
                  {card.href} · {card.cta_text}
                </div>
                <div className="text-[11px] text-slate-500">
                  Bağlı kategori: {gameCategoryLabel(card.game_category)}
                  {' · '}Cihaz: {deviceLabel(card.device)}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(card)} className="admin-btn-primary flex-1 py-2 text-xs flex items-center justify-center gap-1">
                    <Edit3 size={12} /> Düzenle
                  </button>
                  <button
                    onClick={() => move(card, -1)}
                    disabled={orderBusy || index === 0}
                    className="icon-btn view"
                    aria-label={`${card.title} kartını öne al`}
                  >
                    <ArrowUp size={13} />
                  </button>
                  <button
                    onClick={() => move(card, 1)}
                    disabled={orderBusy || index === visibleItems.length - 1}
                    className="icon-btn view"
                    aria-label={`${card.title} kartını geri al`}
                  >
                    <ArrowDown size={13} />
                  </button>
                  <button onClick={() => toggle(card)} className="icon-btn view">
                    {card.is_active ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                  <button onClick={() => remove(card)} className="icon-btn ban"><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="absolute inset-0" style={{ background: 'var(--v2-surface, rgba(0,0,0,0.75))', backdropFilter: 'blur(6px)' }} />
          <div className="relative admin-card w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/5 flex items-center gap-2">
              <LayoutGrid size={16} className="text-cyan-400" />
              <h3 className="font-bold">{modal.mode === 'new' ? 'Yeni Kategori Kartı' : 'Kartı Düzenle'}</h3>
              <button onClick={() => setModal(null)} className="icon-btn view ml-auto"><X size={14} /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              <F l="Başlık (ör: SLOT)">
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="admin-input" />
              </F>
              <F l="Alt Başlık (ör: OYUNLARI)">
                <input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="admin-input" />
              </F>
              <F l="Buton Metni">
                <input value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} className="admin-input" />
              </F>
              <F l="Bağlantı (ör: /slotlar)">
                <input value={form.href} onChange={(e) => setForm({ ...form, href: e.target.value })} className="admin-input" placeholder="/slotlar" />
              </F>
              <F l="Bağlı Oyun Kategorisi">
                <select
                  value={form.game_category}
                  onChange={(e) => setForm({ ...form, game_category: e.target.value })}
                  className="admin-input"
                >
                  {gameCategoryOptions.map(([id, label]) => (
                    <option key={id} value={id}>{label}</option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-slate-500">
                  Seçilen kategori kapalıysa kart ana sayfada gizlenir.
                </p>
              </F>
              <F l="Görünür Olduğu Cihaz">
                <select
                  value={form.device || 'all'}
                  onChange={(e) => setForm({ ...form, device: e.target.value })}
                  className="admin-input"
                >
                  {deviceOptions.map(([id, label]) => (
                    <option key={id} value={id}>{label}</option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-slate-500">
                  Masaüstü ve mobilde ayrı kart seti için kullanın (mobil: 1024 px ve altı).
                </p>
              </F>
              <F l="İkon (klasik tema)">
                <select
                  value={form.card_key}
                  onChange={(e) => setForm({ ...form, card_key: e.target.value })}
                  className="admin-input"
                >
                  {cardKeyOptions.map(([id, label]) => (
                    <option key={id} value={id}>{label}</option>
                  ))}
                </select>
              </F>
              <F l="Kart Görseli" full>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input
                    value={form.image}
                    onChange={(e) => setForm({ ...form, image: e.target.value })}
                    className="admin-input min-w-0 flex-1"
                    placeholder="Görsel URL'si girin"
                  />
                  <label className={`admin-btn-secondary flex shrink-0 cursor-pointer items-center justify-center gap-2 ${uploading ? 'pointer-events-none opacity-60' : ''}`}>
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    {uploading ? 'Yükleniyor...' : 'Bilgisayardan Yükle'}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml,.svg"
                      className="sr-only"
                      disabled={uploading}
                      onChange={uploadImage}
                    />
                  </label>
                </div>
                <p className="mt-1 text-[11px] text-slate-500">
                  Önerilen ölçü 800×350 (16:7) · JPG, PNG, GIF, WEBP, SVG · en fazla {UPLOAD_MAX_LABEL}
                </p>
              </F>
              {form.image && (
                <div className="col-span-2 aspect-[16/7] rounded-lg overflow-hidden bg-slate-800">
                  <img src={resolveApiAssetUrl(form.image)} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <label className="col-span-2 flex items-center gap-2 text-sm">
                <input type="checkbox" className="accent-cyan-500" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                Yayında
              </label>
              <button disabled={busy || uploading} onClick={save} className="admin-btn-primary col-span-2 py-3">
                {busy && <Loader2 size={14} className="animate-spin inline mr-2" />}Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const F = ({ l, children, full }) => (
  <div className={full ? 'col-span-2' : ''}>
    <label className="text-xs text-slate-400 block mb-1">{l}</label>
    {children}
  </div>
);

export default HomeCategoryCards;
